import React, { useEffect, useState, useMemo } from "react";
import {
  Trash2,
  Edit,
  Search,
  CheckCircle,
  XCircle,
  AlertTriangle,
  ChevronLeft,
  ChevronRight,
  Table2,
  PlusCircle,
} from "lucide-react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import Apiurl from "../environment/Apiurl";

function Expenses({ toggleSidebar }) {
  const navigate = useNavigate();
  const [responseData, setResponseData] = useState([]);
  const [departmentData, setDepartmentData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingAdd, setLoadingAdd] = useState(false);
  const [loadingEdit, setLoadingEdit] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [error, setError] = useState(null);
  const [loadingPrivileges, setLoadingPrivileges] = useState(true);
  const [userPrivileges, setUserPrivileges] = useState({});
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [showEntries, setShowEntries] = useState("10");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showPreviewModal, setShowPreviewModal] = useState(false);

  // Form state for adding
  const [addData, setAddData] = useState({
    date: new Date().toISOString().split("T")[0],
    department: "",
    type: "",
    description: "",
    amount: "",
    addedBy: localStorage.getItem("name") || "unknown",
    status: "Pending",
  });

  // Form state for editing
  const [updateData, setUpdateData] = useState({
    date: "",
    department: "",
    type: "",
    description: "",
    amount: "",
    addedBy: localStorage.getItem("user_id") || "unknown",
    status: "Pending",
  });

  // Fetch user privileges based on role_name
  const fetchUserPrivileges = async () => {
    try {
      setLoadingPrivileges(true);
      const userRole = localStorage.getItem("role_name") || "unknown";
      console.log("Fetching privileges for role_name:", userRole);

      if (userRole === "unknown") {
        console.warn("No role_name found in localStorage");
        setError(
          "No role assigned to the user. Please contact the administrator."
        );
        setTimeout(() => setError(null), 3000);
        return;
      }

      const response = await axios.get(`${Apiurl}/roles_and_privillage`);
      console.log(
        "Fetched roles data:",
        JSON.stringify(response.data, null, 2)
      );

      if (!response.data || !Array.isArray(response.data)) {
        throw new Error(
          `Invalid or empty roles data received: ${JSON.stringify(
            response.data
          )}`
        );
      }

      const roleData = response.data.find(
        (role) => role.role_name === userRole
      );

      if (roleData) {
        console.log(
          "Found role data for role_name:",
          userRole,
          JSON.stringify(roleData, null, 2)
        );
        const privileges = {};
        roleData.pages.forEach((page) => {
          const pageName = page.page;
          const pagePrivileges = page.priviallages || page.privileges || {};
          privileges[pageName] = {
            access: page.access,
            ...pagePrivileges,
          };
          console.log(
            `Processing page: ${pageName}, Access: ${page.access}, Privileges:`,
            JSON.stringify(pagePrivileges, null, 2)
          );
        });
        console.log(
          "Set user privileges:",
          JSON.stringify(privileges, null, 2)
        );
        setUserPrivileges(privileges);
      } else {
        console.warn("No role data found for role_name:", userRole);
        setError("No role data found for the current user.");
        setTimeout(() => setError(null), 3000);
      }
    } catch (error) {
      console.error(
        "Error fetching user privileges:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch user privileges. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingPrivileges(false);
    }
  };

  // Check if user has specific permission for Expenses page
  const hasPermission = (permission) => {
    const expensesPrivileges = userPrivileges["Expenses"];
    if (!expensesPrivileges) {
      console.log("No privileges found for Expenses page");
      return false;
    }
    console.log(
      `Checking permission '${permission}' for Expenses:`,
      JSON.stringify(expensesPrivileges, null, 2)
    );
    if (permission === "access") {
      const accessGranted = expensesPrivileges.access === true;
      console.log(`Access permission result: ${accessGranted}`);
      return accessGranted;
    }
    const hasPerm =
      expensesPrivileges.all === true ||
      expensesPrivileges[permission] === true;
    console.log(`Permission '${permission}' result: ${hasPerm}`);
    return hasPerm;
  };

  // Fetch expenses data
  const fetchExpensesData = async () => {
    if (!hasPermission("read")) {
      console.warn("Read permission denied for Expenses page");
      setError("You do not have permission to view expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoading(true);
    try {
      console.log("Fetching expenses data from:", `${Apiurl}/expense`);
      const response = await axios.get(`${Apiurl}/expense`);
      console.log(
        "Fetched expenses data:",
        JSON.stringify(response.data, null, 2)
      );
      setResponseData(response.data.reverse() || []);
      setError(null);
    } catch (error) {
      console.error(
        "Error fetching expenses:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to fetch expenses. Please try again.");
      setTimeout(() => setError(null), 3000);
      setResponseData([]);
    } finally {
      setLoading(false);
    }
  };

  // Get Department data
  const fetchDepartment = async () => {
    try {
      const departmentData = await axios.get(`${Apiurl}/department`);
      console.log("Department Datas", departmentData);
      setDepartmentData(departmentData.data || []);
    } catch (error) {
      console.log(error.message);
    }
  };

  useEffect(() => {
    fetchDepartment();
  }, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Initialize privileges and data
  useEffect(() => {
    console.log("Fetching initial privileges...");
    fetchUserPrivileges();
  }, []);

  useEffect(() => {
    if (!loadingPrivileges && hasPermission("access")) {
      console.log("Access granted for Expenses page");
      if (hasPermission("read")) {
        console.log("Fetching expenses data due to read permission");
        fetchExpensesData();
      } else {
        console.warn("Read permission denied for Expenses page");
        setError("You do not have permission to view expense records.");
        setTimeout(() => setError(null), 3000);
      }
    } else if (!loadingPrivileges) {
      console.warn("Access permission denied for Expenses page");
      setError("You do not have permission to access this page.");
      setTimeout(() => setError(null), 3000);
    }
  }, [userPrivileges, loadingPrivileges]);

  // Filter data
  const filteredData = useMemo(() => {
    const result = responseData.filter((item) =>
      [
        item.date,
        item.department,
        item.type,
        item.description,
        item.addedBy,
        item.status,
        item.amount,
      ].some((prop) =>
        prop != null
          ? prop.toString().toLowerCase().includes(searchTerm.toLowerCase())
          : false
      )
    );
    console.log("Filtered expenses data:", result);
    return result;
  }, [responseData, searchTerm]);

  // Update items per page
  useEffect(() => {
    console.log("Updating items per page to:", showEntries);
    if (showEntries === "All") {
      setItemsPerPage(filteredData.length || 1);
    } else {
      setItemsPerPage(Number(showEntries));
    }
  }, [showEntries, filteredData]);

  // Reset to first page when search term or items per page changes
  useEffect(() => {
    console.log(
      "Resetting current page due to searchTerm or itemsPerPage change"
    );
    setCurrentPage(1);
  }, [searchTerm, itemsPerPage]);

  // Calculate pagination values
  const totalPages = Math.ceil(filteredData.length / itemsPerPage) || 1;
  console.log(
    "Pagination - totalPages:",
    totalPages,
    "itemsPerPage:",
    itemsPerPage,
    "filteredData.length:",
    filteredData.length
  );
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);
  console.log(
    "Current items:",
    currentItems,
    "from index",
    indexOfFirstItem,
    "to",
    indexOfLastItem
  );

  // Generate page list for pagination
  const getPageList = () => {
    const pages = [];
    if (totalPages <= 7) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 4) {
        pages.push(1, 2, 3, 4, 5, "...", totalPages);
      } else if (currentPage >= totalPages - 3) {
        pages.push(
          1,
          "...",
          totalPages - 4,
          totalPages - 3,
          totalPages - 2,
          totalPages - 1,
          totalPages
        );
      } else {
        pages.push(
          1,
          "...",
          currentPage - 1,
          currentPage,
          currentPage + 1,
          "...",
          totalPages
        );
      }
    }
    console.log("Generated page list:", pages);
    return pages;
  };

  const pages = getPageList();

  const handlePageClick = (page) => {
    if (page !== "..." && page !== currentPage) {
      console.log("Navigating to page:", page);
      setCurrentPage(page);
    }
  };

  // Validate form data
  const validateForm = (data) => {
    if (!data.date) return "Date is required.";
    if (!data.department) return "Department is required.";
    if (!data.type) return "Type is required.";
    if (!data.description) return "Description is required.";
    if (!data.amount) return "Amount is required.";
    if (isNaN(data.amount) || Number(data.amount) <= 0)
      return "Amount must be a positive number.";
    if (!data.addedBy) return "Added By is required.";
    if (!data.status) return "Status is required.";
    return null;
  };

  // Handle input changes for add form
  const handleAddInputChange = (e) => {
    const { name, value } = e.target;
    setAddData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle input changes for edit form
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setUpdateData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Handle add form submission
  const handleAddSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Expenses page");
      setError("You do not have permission to add expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(addData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingAdd(true);
    try {
      console.log(
        "Adding expense with data:",
        JSON.stringify(addData, null, 2)
      );
      await axios.post(`${Apiurl}/expense`, addData);
      await fetchExpensesData();
      closeAddModal();
    } catch (error) {
      console.error(
        "Error adding expense:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to add expense. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingAdd(false);
    }
  };

  // Handle edit form submission
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Expenses page");
      setError("You do not have permission to update expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!editingId) {
      console.warn("Invalid expense record ID:", editingId);
      setError("Invalid expense record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    const validationError = validateForm(updateData);
    if (validationError) {
      console.warn("Validation error:", validationError);
      setError(validationError);
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingEdit(true);
    try {
      console.log(
        "Updating expense with ID:",
        editingId,
        "data:",
        JSON.stringify(updateData, null, 2)
      );
      await axios.put(`${Apiurl}/expense/${editingId}`, updateData);
      await fetchExpensesData();
      closeEditModal();
    } catch (error) {
      console.error(
        "Error updating expense:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to update expense. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingEdit(false);
    }
  };

  // Handle delete expense
  const deleteExpense = async () => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Expenses page");
      setError("You do not have permission to delete expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!deleteId) {
      console.warn("Invalid expense record ID:", deleteId);
      setError("Invalid expense record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    setLoadingDelete(true);
    try {
      console.log("Deleting expense with ID:", deleteId);
      await axios.delete(`${Apiurl}/expense/${deleteId}`);
      await fetchExpensesData();
      closeDeleteModal();
    } catch (error) {
      console.error(
        "Error deleting expense:",
        error.message,
        error.response
          ? JSON.stringify(error.response.data, null, 2)
          : "No response data"
      );
      setError("Failed to delete expense. Please try again.");
      setTimeout(() => setError(null), 3000);
    } finally {
      setLoadingDelete(false);
    }
  };

  // Edit expense
  const handleEdit = (id, item) => {
    if (!hasPermission("update")) {
      console.warn("Update permission denied for Expenses page");
      setError("You do not have permission to update expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid expense record ID:", id);
      setError("Invalid expense record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Edit Expense modal for ID:", id, "data:", item);
    setEditingId(id);
    setUpdateData({
      date: item.date ? new Date(item.date).toISOString().split("T")[0] : "",
      department: item.department || "",
      type: item.type || "",
      description: item.description || "",
      amount: item.amount || "",
      addedBy: localStorage.getItem("user_id") || "unknown",
      status: item.status || "Pending",
    });
    setShowEditModal(true);
  };

  // Preview expense
  const handlePreview = (expense) => {
    if (!hasPermission("download")) {
      console.warn("Download permission denied for Expenses page");
      setError("You do not have permission to view expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!expense._id) {
      console.warn("Invalid expense record ID:", expense._id);
      setError("Invalid expense record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log(
      "Navigating to View Expense page for ID:",
      expense._id,
      "data:",
      expense
    );
    toggleSidebar();
    navigate(`/expenses/view-expense/${expense._id}`, {
      state: { expense },
    });
  };

  // Open delete confirmation modal
  const openDeleteModal = (id) => {
    if (!hasPermission("delete")) {
      console.warn("Delete permission denied for Expenses page");
      setError("You do not have permission to delete expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    if (!id) {
      console.warn("Invalid expense record ID:", id);
      setError("Invalid expense record ID.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Delete Expense modal for ID:", id);
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  // Close delete confirmation modal
  const closeDeleteModal = () => {
    console.log("Closing Delete Expense modal");
    setShowDeleteModal(false);
    setDeleteId(null);
    setError(null);
  };

  // Open add modal
  const openAddModal = () => {
    if (!hasPermission("create")) {
      console.warn("Create permission denied for Expenses page");
      setError("You do not have permission to add expense records.");
      setTimeout(() => setError(null), 3000);
      return;
    }
    console.log("Opening Add Expense modal");
    setShowAddModal(true);
  };

  // Close add modal
  const closeAddModal = () => {
    console.log("Closing Add Expense modal");
    setShowAddModal(false);
    setAddData({
      date: new Date().toISOString().split("T")[0],
      department: "",
      type: "",
      description: "",
      amount: "",
      addedBy: localStorage.getItem("user_id") || "unknown",
      status: "Pending",
    });
    setError(null);
  };

  // Close edit modal
  const closeEditModal = () => {
    console.log("Closing Edit Expense modal");
    setShowEditModal(false);
    setEditingId(null);
    setUpdateData({
      date: "",
      department: "",
      type: "",
      description: "",
      amount: "",
      addedBy: localStorage.getItem("user_id") || "unknown",
      status: "Pending",
    });
    setError(null);
  };

  // Close preview modal
  const closePreviewModal = () => {
    console.log("Closing Preview Expense modal");
    setShowPreviewModal(false);
    setSelectedExpense(null);
    setError(null);
  };

  if (loadingPrivileges) {
    return <div className="p-3 text-center">Loading permissions...</div>;
  }

  if (!hasPermission("access")) {
    console.log(
      "Access denied for role_name:",
      localStorage.getItem("role_name"),
      "on Expenses page"
    );
    return (
      <div className="p-3">
        <div className="alert alert-danger">
          You don&apos;t have permission to access this page.
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid py-3 px-2">
      <style>{`
        .btn-custom {
          background-color: #01AEBE;
          color: white;
          border: none;
        }
        .btn-custom:hover {
          background-color: #018A97;
          color: white;
        }
      `}</style>

      {hasPermission("create") && (
        <div className="d-flex justify-content-end align-items-center mb-3">
          <button className="btn btn-custom btn-sm" onClick={openAddModal}>
            <PlusCircle size={16} className="me-2" /> Add Expense
          </button>
        </div>
      )}

      {error && (
        <div className="alert alert-danger alert-dismissible fade show mt-2">
          {error}
          <button
            type="button"
            className="btn-close"
            onClick={() => setError(null)}
          ></button>
        </div>
      )}

      {hasPermission("read") && (
        <div className="card card-body mt-2">
          <div className="row mb-2 crm-table-toolbar">
            <div className="col-md-2">
              <div className="input-group">
                <span className="input-group-text">
                  <Table2 size={16} />
                </span>
                <select
                  className="form-select"
                  value={showEntries}
                  onChange={(e) => {
                    console.log(
                      "Changing entries per page to:",
                      e.target.value
                    );
                    setShowEntries(e.target.value);
                  }}
                >
                  <option value="10">Show 10</option>
                  <option value="20">Show 20</option>
                  <option value="50">Show 50</option>
                  <option value="100">Show 100</option>
                  <option value="200">Show 200</option>
                  <option value="500">Show 500</option>
                  <option value="1000">Show 1000</option>
                  <option value="All">Show All</option>
                </select>
              </div>
            </div>
            <div className="col-md-7"></div>
            <div className="col-md-3">
              <div className="d-flex align-items-center">
                <div className="position-relative">
                  <input
                    type="text"
                    className="form-control pe-5"
                    placeholder="Search by date, department, type, description, added by, or status"
                    value={searchTerm}
                    onChange={(e) => {
                      console.log("Search term changed to:", e.target.value);
                      setSearchTerm(e.target.value);
                    }}
                    style={{ width: "250px", paddingRight: "2.5rem" }}
                  />
                  {searchTerm && (
                    <button
                      className="position-absolute bg-transparent border-0"
                      style={{
                        right: "30px",
                        top: "50%",
                        transform: "translateY(-50%)",
                      }}
                      onClick={() => setSearchTerm("")}
                    >
                      <XCircle size={16} className="text-muted" />
                    </button>
                  )}
                  <Search
                    size={16}
                    className="position-absolute text-muted"
                    style={{
                      right: "10px",
                      top: "35%",
                      transform: "translateY(-50%)",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {loading && <div className="text-center py-3">Loading...</div>}

          <div className="table-responsive mt-3">
            <table className="table table-hover">
              <thead style={{ backgroundColor: "#01AEBE", color: "white" }}>
                <tr>
                  <th>S.No</th>
                  <th>Date</th>
                  <th>Department</th>
                  <th>Type</th>
                  <th>Description</th>
                  <th>Amount (₹)</th>
                  <th>Added By</th>
                  <th>Status</th>
                  {(hasPermission("download") ||
                    hasPermission("update") ||
                    hasPermission("delete")) && <th>Action</th>}
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentItems.map((item, index) => (
                    <tr key={item._id || index} className="align-middle">
                      <td>{indexOfFirstItem + index + 1}</td>
                      <td>
                        {item.date
                          ? new Date(item.date).toLocaleDateString()
                          : "N/A"}
                      </td>
                      <td>{item.department || "N/A"}</td>
                      <td>{item.type || "N/A"}</td>
                      <td>{item.description || "N/A"}</td>
                      <td>₹{item.amount || "0"}</td>
                      <td>{item.addedBy || "N/A"}</td>
                      <td>
                        <span
                          className={`badge rounded-pill px-3 py-2 fw-semibold d-inline-flex align-items-center ${
                            item.status?.toLowerCase() === "paid"
                              ? "bg-success text-white"
                              : "bg-warning text-dark"
                          }`}
                        >
                          {item.status?.toLowerCase() === "paid" ? (
                            <CheckCircle size={16} className="me-2" />
                          ) : (
                            <XCircle size={16} className="me-2" />
                          )}
                          {item.status?.toLowerCase() === "paid"
                            ? "Paid"
                            : "Pending"}
                        </span>
                      </td>
                      {(hasPermission("download") ||
                        hasPermission("update") ||
                        hasPermission("delete")) && (
                        <td>
                          {hasPermission("delete") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Delete"
                              onClick={() => openDeleteModal(item._id)}
                              disabled={!item._id}
                            >
                              <Trash2 size={16} className="text-danger" />
                            </button>
                          )}
                          {hasPermission("update") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="Edit"
                              onClick={() => handleEdit(item._id, item)}
                              disabled={!item._id}
                            >
                              <Edit size={16} className="text-primary" />
                            </button>
                          )}
                          {/* {hasPermission("download") && (
                            <button
                              className="btn btn-sm p-1 border-0"
                              title="View"
                              onClick={() => handlePreview(item)}
                              disabled={!item._id}
                            >
                              <Eye size={16} className="text-success" />
                            </button>
                          )} */}
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td
                      colSpan={
                        hasPermission("download") ||
                        hasPermission("update") ||
                        hasPermission("delete")
                          ? 9
                          : 8
                      }
                      className="text-center py-4"
                    >
                      {loading ? (
                        "Loading..."
                      ) : (
                        <div className="text-muted">
                          <Search size={24} className="mb-2" />
                          <br />
                          No expense records found matching your search criteria
                        </div>
                      )}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            {filteredData.length > 0 && showEntries !== "All" && (
              <div className="container">
                <div className="d-flex justify-content-between align-items-center p-3 border-top gap-2">
                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage > 1 && handlePageClick(currentPage - 1)
                    }
                    disabled={currentPage === 1}
                  >
                    <ChevronLeft size={16} />
                    <span className="d-none d-sm-inline">Previous</span>
                  </button>

                  <div
                    className="d-flex overflow-auto flex-nowrap gap-2 px-2"
                    style={{ maxWidth: "100%" }}
                  >
                    {pages.map((page, index) => (
                      <button
                        key={index}
                        className={`btn btn-sm px-3 flex-shrink-0 ${
                          page === currentPage
                            ? "text-white"
                            : "btn-outline-secondary"
                        }`}
                        style={
                          page === currentPage
                            ? { backgroundColor: "#01AEBE", border: "none" }
                            : {}
                        }
                        onClick={() => handlePageClick(page)}
                        disabled={page === "..."}
                      >
                        {page}
                      </button>
                    ))}
                  </div>

                  <button
                    className="btn btn-outline-secondary d-flex align-items-center gap-2"
                    onClick={() =>
                      currentPage < totalPages &&
                      handlePageClick(currentPage + 1)
                    }
                    disabled={currentPage === totalPages}
                  >
                    <span className="d-none d-sm-inline">Next</span>
                    <ChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {hasPermission("create") && (
        <div
          className={`modal fade ${showAddModal ? "show" : ""}`}
          style={{ display: showAddModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="addExpenseModalLabel"
          aria-hidden={!showAddModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6 className="modal-title text-dark" id="addExpenseModalLabel">
                  Add Expense
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeAddModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleAddSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={addData.date}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Type</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Expense Type"
                        name="type"
                        value={addData.type}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    {/* <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Department</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Department"
                        name="department"
                        value={addData.department}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div> */}

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Department</label>
                      <select
                        className="form-select"
                        name="department"
                        value={addData.department || ""}
                        onChange={handleAddInputChange}
                        required
                      >
                        <option value="">Select Department</option>
                        {departmentData.map((dept) => (
                          <option key={dept._id} value={dept.department}>
                            {dept.department}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Added By</label>
                      <input
                        type="text"
                        className="form-control"
                        name="addedBy"
                        value={addData.addedBy}
                        disabled
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Description"
                        name="description"
                        value={addData.description}
                        onChange={handleAddInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Amount (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Amount"
                        name="amount"
                        value={addData.amount}
                        onChange={handleAddInputChange}
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={addData.status}
                        onChange={handleAddInputChange}
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Paid">Paid</option>
                      </select>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeAddModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loadingAdd}
                    >
                      {loadingAdd ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("update") && (
        <div
          className={`modal fade ${showEditModal ? "show" : ""}`}
          style={{ display: showEditModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="editExpenseModalLabel"
          aria-hidden={!showEditModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6
                  className="modal-title text-dark"
                  id="editExpenseModalLabel"
                >
                  Edit Expense
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeEditModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleEditSubmit}>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Date</label>
                      <input
                        type="date"
                        className="form-control"
                        name="date"
                        value={updateData.date}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Type</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Expense Type"
                        name="type"
                        value={updateData.type}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    {/* <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Department</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Department"
                        name="department"
                        value={updateData.department}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div> */}

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Department</label>
                      <select
                        className="form-select"
                        name="department"
                        value={updateData.department || ""}
                        onChange={handleEditInputChange}
                        required
                      >
                        <option value="">Select Department</option>
                        {departmentData.map((dept) => (
                          <option key={dept._id} value={dept.department}>
                            {dept.department}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Added By</label>
                      <input
                        type="text"
                        className="form-control"
                        name="addedBy"
                        value={updateData.addedBy}
                        disabled
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Description</label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter Description"
                        name="description"
                        value={updateData.description}
                        onChange={handleEditInputChange}
                        required
                      />
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Amount (₹)</label>
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter Amount"
                        name="amount"
                        value={updateData.amount}
                        onChange={handleEditInputChange}
                        min="0.01"
                        step="0.01"
                        required
                      />
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Status</label>
                      <select
                        className="form-control"
                        name="status"
                        value={updateData.status}
                        onChange={handleEditInputChange}
                        required
                      >
                        <option value="">Select Status</option>
                        <option value="Pending">Pending</option>
                        <option value="Paid">Paid</option>
                      </select>
                    </div>
                  </div>
                  <div className="text-center mt-4">
                    <button
                      type="button"
                      className="btn btn-secondary btn-sm me-2"
                      style={{ backgroundColor: "#e0e0e0", color: "#333" }}
                      onClick={closeEditModal}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="btn btn-custom btn-sm px-4"
                      disabled={loadingEdit}
                    >
                      {loadingEdit ? "Updating..." : "Update"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("delete") && (
        <div
          className={`modal fade ${showDeleteModal ? "show" : ""}`}
          style={{ display: showDeleteModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="deleteExpenseModalLabel"
          aria-hidden={!showDeleteModal}
        >
          <div className="modal-dialog modal-sm modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closeDeleteModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body text-center">
                <div style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      display: "inline-block",
                      width: "40px",
                      height: "40px",
                      backgroundColor: "#fee2e2",
                      borderRadius: "50%",
                      textAlign: "center",
                      lineHeight: "40px",
                    }}
                  >
                    <AlertTriangle size={20} style={{ color: "#dc2626" }} />
                  </span>
                </div>
                <h5 className="modal-title" id="deleteExpenseModalLabel">
                  Delete Expense
                </h5>
                <p style={{ marginTop: "10px" }}>
                  Are you sure you want to delete this expense record?
                </p>
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-secondary btn-sm"
                  style={{
                    marginRight: "10px",
                    backgroundColor: "#e0e0e0",
                    color: "#333",
                  }}
                  onClick={closeDeleteModal}
                >
                  No, Keep it
                </button>
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={deleteExpense}
                  disabled={loadingDelete}
                >
                  {loadingDelete ? "Deleting..." : "Yes, Delete It!"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasPermission("download") && (
        <div
          className={`modal fade ${showPreviewModal ? "show" : ""}`}
          style={{ display: showPreviewModal ? "block" : "none" }}
          tabIndex="-1"
          aria-labelledby="previewExpenseModalLabel"
          aria-hidden={!showPreviewModal}
        >
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content" style={{ borderRadius: "10px" }}>
              <div className="modal-header" style={{ borderBottom: "none" }}>
                <h6
                  className="modal-title text-dark"
                  id="previewExpenseModalLabel"
                >
                  Expense Details
                </h6>
                <button
                  type="button"
                  className="btn-close"
                  style={{ fontSize: "10px" }}
                  onClick={closePreviewModal}
                  aria-label="Close"
                ></button>
              </div>
              <div className="modal-body">
                {selectedExpense && (
                  <div className="row">
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Date</label>
                      <p>
                        {selectedExpense.date
                          ? new Date(selectedExpense.date).toLocaleDateString()
                          : "N/A"}
                      </p>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Type</label>
                      <p>{selectedExpense.type || "N/A"}</p>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Department</label>
                      <p>{selectedExpense.department || "N/A"}</p>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Added By</label>
                      <p>{selectedExpense.addedBy || "N/A"}</p>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Description</label>
                      <p>{selectedExpense.description || "N/A"}</p>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Amount (₹)</label>
                      <p>₹{selectedExpense.amount || "0"}</p>
                    </div>
                    <div className="col-md-6 mb-3">
                      <label className="form-label fw-bold">Status</label>
                      <p>{selectedExpense.status || "N/A"}</p>
                    </div>
                  </div>
                )}
              </div>
              <div
                className="modal-footer"
                style={{ borderTop: "none", justifyContent: "center" }}
              >
                <button
                  type="button"
                  className="btn btn-custom btn-sm"
                  onClick={closePreviewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {(showAddModal && hasPermission("create")) ||
      (showEditModal && hasPermission("update")) ||
      (showDeleteModal && hasPermission("delete")) ||
      (showPreviewModal && hasPermission("download")) ? (
        <div className="modal-backdrop fade show"></div>
      ) : null}
    </div>
  );
}

export default Expenses;
